import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { postOrder } from "../../app/services/orderService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import Form from "react-bootstrap/Form";
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Modal,
  Table,
} from "react-bootstrap";
import {
  Check,
  CheckAll,
  ExclamationTriangle,
  Pen,
  Plus,
  X,
} from "react-bootstrap-icons";
import { Article, defaultArticle } from "../../models/responses/article";
import { getDefaultOrder, Order } from "../../models/responses/order";
import {
  getOrderStatusById,
  getOrderStatuses,
} from "../../app/services/orderStatusService";
import { setClientToggleLocked } from "../../app/slices/clientTypeSlice";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Customer } from "../../models/responses/customer";
import { getCustomers } from "../../app/services/customerService";
import { OrderStatus } from "../../models/responses/orderStatus";
import { OrderType } from "../../models/responses/orderType";
import { getOrderTypes } from "../../app/services/orderTypeService";
import { Supplier } from "../../models/responses/supplier";
import { getSuppliers } from "../../app/services/supplierService";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import { getCarBrands } from "../../app/services/carBrandService";
import { getBrandDiscountByCarBrandId } from "../../app/services/brandDiscountService";
import { getArticleTemplatesByType } from "../../app/services/articleTemplateService";
import OrderFields, { OrderFieldsHandlers, OrderFieldsLists, OrderFieldsLoadingStates } from "../../components/orders/orderFields";
import EditableArticleModal from "../../components/orders/editableArticleModal";
import { useArticleTableHandlers, useArticleModalHandlers } from "../../app/hooks/useArticleHandlers";

interface Props { }

const OrderAdd: FC<Props> = () => {
  // Router
  const { orderStatusId } = useParams<{ orderStatusId: string }>();
  const navigate = useNavigate();

  // Auth
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  // Redux
  const dispatch = useAppDispatch();
  const orderStatus: OrderStatus = useSelector((state: RootState) => state.orderStatus.orderStatus);
  const orderStatusList: OrderStatus[] = useSelector((state: RootState) => state.orderStatus.setOrderStatusList);
  const clientType = useSelector((state: RootState) => state.clientType.clientType);
  const customers: Customer[] = useSelector((state: RootState) => state.customer.customerList);
  const orderTypes: OrderType[] = useSelector((state: RootState) => state.orderTypes.orderTypeList);
  const suppliers: Supplier[] = useSelector((state: RootState) => state.supplier.supplierList);
  const carBrands = useSelector((state: RootState) => state.carBrand.carBrandList);
  const brandDiscount = useSelector((state: RootState) => state.brandDiscount.brandDiscount);
  const articleTemplateListTime = useSelector((state: RootState) => state.articleTemplate.articleTemplateListTime);
  const articleTemplateListProduct = useSelector((state: RootState) => state.articleTemplate.articleTemplateListProduct);
  const isWaitingOrderPostResponse = useSelector((state: RootState) => state.order.isWaitingOrderPostResponse);
  const isLoadingCarBrands = useSelector((state: RootState) => state.carBrand.isWaitingCarBrandGetResponse);

  // Local state
  const [orderData, setOrderData] = useState<Order>({ ...getDefaultOrder(clientType), customerId: "" });
  const [modalArticle, setModalArticle] = useState<Article>({ ...defaultArticle });
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [allVerified, setAllVerified] = useState(false);
  const [showAlreadyExistsModal, setShowAlreadyExistsModal] = useState(false);
  const [pendingOverrideOrder, setPendingOverrideOrder] = useState<Order | null>(null);
  const [alreadyExistsMessage, setAlreadyExistsMessage] = useState<string>("");
  const [displayError, setDisplayError] = useState(false);

  // Article handlers from hooks
  const articleTableHandlers = useArticleTableHandlers({
    orderData,
    setOrderData,
    brandDiscount,
  });

  const articleModalHandlers = useArticleModalHandlers({
    orderData,
    setOrderData,
    modalArticle,
    setModalArticle,
    setShowArticleModal,
    brandDiscount,
    suppliers,
  });

  // Effects - Initial data loading
  useEffect(() => {
    dispatch(setClientToggleLocked(true));
    dispatch(getCarBrands(instance, account));
  }, [instance, account, dispatch]);

  useEffect(() => {
    dispatch(getOrderStatusById(instance, account, orderStatusId));
  }, [account, accounts, dispatch, instance, orderStatusId]);

  useEffect(() => {
    dispatch(getOrderStatuses(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getCustomers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getOrderTypes(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getSuppliers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Time));
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Product));
  }, [account, dispatch, instance]);

  useEffect(() => {
    dispatch(getBrandDiscountByCarBrandId(instance, account, orderData?.selectedCarBrandId || ""));
  }, [dispatch, instance, account, orderData?.selectedCarBrandId]);

  // Effects - Data synchronization
  useEffect(() => {
    if (orderStatusList.length > 0) {
      orderData.orderStatusId = orderStatusList[0].id;
    }
  }, [orderStatusList]);

  useEffect(() => {
    if (orderTypes.length > 0) {
      setOrderData((prev) => ({
        ...prev,
        orderTypeId: orderTypes[0].id,
        discount: orderTypes[0].discount ?? null,
      }));
    }
  }, [orderTypes]);

  useEffect(() => {
    // Recalculate prices when brandDiscount changes
    orderData.articles?.forEach((article) => {
      articleTableHandlers.onSalesPriceChange(article.rowNumber, article.salesPrice);
    });
  }, [brandDiscount]);

  useEffect(() => {
    checkVerified();
  }, [orderData.articles]);

  useEffect(() => {
    setModalArticle({
      ...modalArticle,
      totalExVat: parseFloat(
        (parseFloat(modalArticle.salesPrice) * modalArticle.quantity).toFixed(2)
      ),
    });
  }, [modalArticle.quantity, modalArticle.salesPrice, modalArticle.vat]);

  // ORDER HANDLERS
  const customerInputHandler = (e: string) => {
    setOrderData({ ...orderData, customerId: e });
  };
  const assignedToInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, assignedTo: e.target.value });
  };
  const registrationNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, registrationNumber: e.target.value });
  };
  const vinInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, vin: e.target.value });
  };
  const carInfoInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, carInfo: e.target.value });
  };
  const aoNumberInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, aoNumber: e.target.value });
  };
  const orderTypeInputHandler = (e: string) => {
    const selectedType = orderTypes.find((type) => type.id === e);
    const newDiscount = selectedType?.discount ?? 0;
    setOrderData({
      ...orderData,
      orderTypeId: e,
      discount: newDiscount,
      articles:
        orderData.articles?.map((article) => ({
          ...article,
          discount: newDiscount,
        })) ?? [],
    });
  };
  const orderDiscountInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = e.target.value;
    if (input === "") {
      setOrderData({
        ...orderData,
        discount: null,
        articles:
          orderData.articles?.map((article) =>
            article.verified ? article : { ...article, discount: null }
          ) ?? [],
      });
    } else {
      const value = Number(input);
      if (value >= 0 && value <= 100) {
        setOrderData({
          ...orderData,
          discount: value,
          articles:
            orderData.articles?.map((article) =>
              article.verified ? article : { ...article, discount: value }
            ) ?? [],
        });
      }
    }
  };
  const emailBodyInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, emailBody: e.target.value });
  };
  const arriveDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, arriveDate: e.target.value });
  };
  const bookingDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, bookingDate: e.target.value });
  };
  const deliveryDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, deliveryDate: e.target.value });
  };
  const orderFilenameInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, filename: e.target.value });
  };
  const commentInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, comment: e.target.value });
  };
  const internalCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, internalComment: e.target.value });
  };
  const priceProposalMessageInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, priceProposalMessage: e.target.value });
  };
  const orderApprovedInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, approved: e.target.checked });
  };
  const orderDeniedInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, denied: e.target.checked });
  };
  const orderStatusInputHandler = (e: string) => {
    setOrderData({ ...orderData, orderStatusId: e });
  };
  const clientExternalOrderIdInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, clientExternalOrderId: e.target.value });
  };
  const workshopIdInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, workshopId: e.target.value });
  };
  const workshopNameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, workshopName: e.target.value });
  };
  const odometerInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, odometer: Number(e.target.value) });
  };
  const clientVehicleRetrieverInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, clientVehicleRetriever: e.target.value });
  };
  const selectedCarBrandInputHandler = (e: string) => {
    if (e === "") {
      setOrderData({ ...orderData, selectedCarBrandId: null });
    } else {
      const selectedBrand = carBrands.find((brand) => brand.id === e);
      setOrderData({
        ...orderData,
        selectedCarBrandId: selectedBrand ? selectedBrand.id : null,
      });
    }
  };

  // ARTICLES - Handlers that need local state (rest is in useArticleHandlers)
  const addNewArticleHandler = () => {
    let newArticle: Article = JSON.parse(JSON.stringify(defaultArticle));
    newArticle.rowNumber = calculateRowNumber();
    const selectedOrderType = orderTypes.find(
      (type) => type.id === orderData.orderTypeId
    );
    newArticle.discount = selectedOrderType?.discount ?? 0;

    let articlesList = Array.isArray(orderData.articles)
      ? orderData.articles
      : [];
    let newList = [...articlesList, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const addNewTimeArticleHandler = () => {
    let newArticle: Article = JSON.parse(JSON.stringify(defaultArticle));
    newArticle.rowNumber = calculateRowNumber();
    newArticle.articleType = ArticleType.Time;

    let articlesList = Array.isArray(orderData.articles)
      ? orderData.articles
      : [];
    let newList = [...articlesList, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const calculateRowNumber = () => {
    if (!orderData.articles || orderData.articles.length === 0) return 1;
    return Math.max(...orderData.articles.map((a) => a.rowNumber)) + 1;
  };

  const articleTemplateHandler = (rowNumber: number, templateId: string, type: ArticleType) => {
    const list = type === ArticleType.Time ? articleTemplateListTime : articleTemplateListProduct;
    const template = list.find(t => t.id === templateId);
    if (!template) return;
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            salesPrice: template.keepIncomingPrice
              ? article.salesPrice
              : template.salesPrice,
            articleNumber: template.articleNumber,
          }
          : article
      ),
    });
  };

  const checkVerified = () => {
    let allVerified = orderData.articles?.every((art) => {
      if (art.verified) return true;
      return false;
    });

    if (allVerified) {
      setAllVerified(true);
    } else {
      setAllVerified(false);
    }
  };


  const handleAddOrder = () => {
    setDisplayError(false);
    if (!orderData.customerId) {
      setDisplayError(true);
      return;
    }
    dispatch(postOrder(instance, account, orderData, onSuccess,
      (errorType, errorMessage) => {
        if (errorType === "ALREADY_EXISTS") {
          setAlreadyExistsMessage(errorMessage || "Det finns redan en order med detta PostNord-id i systemet.");
          setPendingOverrideOrder({ ...orderData });
          setShowAlreadyExistsModal(true);
        } else {
          setDisplayError(true);
        }
      }
    ));
  };

  const handleOverrideOrder = () => {
    if (pendingOverrideOrder) {
      const overrideOrder = { ...pendingOverrideOrder, overrideException: true };
      setShowAlreadyExistsModal(false);
      setPendingOverrideOrder(null);
      dispatch(postOrder(instance, account, overrideOrder, onSuccess,
        (errorType, errorMessage) => {
          setDisplayError(true);
        }
      ));
    }
  };

  const handleCancelOverride = () => {
    setShowAlreadyExistsModal(false);
    setPendingOverrideOrder(null);
  };

  const onSuccess = (orderId: string | null) => {
    if (orderId === null) {
      setDisplayError(true);
      return;
    }

    setTimeout(() => {
      navigate(`../view/${orderId}`, { relative: "path" });
    }, 500);
  };

  // Handler objects for OrderFields
  const orderFieldsHandlers: OrderFieldsHandlers = {
    onStatusChange: orderStatusInputHandler,
    onCustomerChange: customerInputHandler,
    onAssignedToChange: assignedToInputHandler,
    onRegistrationNumberChange: registrationNumberInputHandler,
    onCarBrandChange: selectedCarBrandInputHandler,
    onVinChange: vinInputHandler,
    onCarInfoChange: carInfoInputHandler,
    onAoNumberChange: aoNumberInputHandler,
    onOrderTypeChange: orderTypeInputHandler,
    onDiscountChange: (discount) => orderDiscountInputHandler({ target: { value: discount === null ? "" : String(discount) } } as React.ChangeEvent<HTMLInputElement>),
    onEmailBodyChange: emailBodyInputHandler,
    onArriveDateChange: arriveDateInputHandler,
    onBookingDateChange: bookingDateInputHandler,
    onDeliveryDateChange: deliveryDateInputHandler,
    onFilenameChange: orderFilenameInputHandler,
    onCommentChange: commentInputHandler,
    onInternalCommentChange: internalCommentInputHandler,
    onPriceProposalMessageChange: priceProposalMessageInputHandler,
    onApprovedChange: orderApprovedInputHandler,
    onDeniedChange: orderDeniedInputHandler,
    onClientExternalOrderIdChange: clientExternalOrderIdInputHandler,
    onOdometerChange: odometerInputHandler,
    onClientVehicleRetrieverChange: clientVehicleRetrieverInputHandler,
    onWorkshopIdChange: workshopIdInputHandler,
    onWorkshopNameChange: workshopNameInputHandler,
  };

  const orderFieldsLists: OrderFieldsLists = {
    orderStatusList,
    customers,
    orderTypes,
    carBrands,
  };

  const orderFieldsLoading: OrderFieldsLoadingStates = {
    isLoadingCarBrands,
  };

  return (
    <div>
      <Modal show={showAlreadyExistsModal} onHide={handleCancelOverride} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Varning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{alreadyExistsMessage || "Det finns redan en order med detta PostNord-id i systemet. Vill du skapa den ändå?"}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelOverride}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleOverrideOrder}>
            Skapa ändå
          </Button>
        </Modal.Footer>
      </Modal>
      <Breadcrumb>
        <Breadcrumb.Item href={`/orderStatus/${orderStatus.id}`}>
          {orderStatus.name}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Ny order</Breadcrumb.Item>
      </Breadcrumb>
      <hr />
      <Form>
        <OrderFields
          orderData={orderData}
          orderStatus={orderStatus}
          clientType={clientType}
          order={orderData}
          onShowCustomerModal={() => {}}
          mode="add"
          wrapInForm={false}
          handlers={orderFieldsHandlers}
          lists={orderFieldsLists}
          loadingStates={orderFieldsLoading}
        />
        <h3>Artiklar</h3>
        <Col xs={12} md={12}>
          <Table responsive striped bordered hover>
            <thead>
              <tr key="-1">
                <th style={{ width: "5%" }}>
                  {allVerified ? (
                    <CheckAll className="text-success" />
                  ) : (
                    <ExclamationTriangle className="text-danger" />
                  )}
                </th>
                {clientType === ClientType.Fleet && (
                  <th>Välj mall</th>
                )}
                <th>Art.nr</th>
                <th>Beskrivning</th>
                <th>Antal</th>
                <th>Försäljningspris</th>
                {(clientType === ClientType.Standard || clientType === ClientType.Fleet) && (
                  <th>Rabatt (%)</th>
                )}
                {clientType === ClientType.Fleet && (
                  <th>Exkluderad</th>
                )}
                <th>
                  <Pen />
                </th>
              </tr>
            </thead>
            <tbody>
              {orderData?.articles
                ?.filter((article) => article.articleType === ArticleType.Product)
                .map((article) => (
                  <tr key={article.rowNumber}>
                    <td>
                      {!article.verified ? (
                        <X className="text-danger" />
                      ) : (
                        <Check className="text-success" />
                      )}
                    </td>
                    {clientType === ClientType.Fleet && (
                      <td>
                        <Form.Select
                          size="sm"
                          className="mt-1"
                          value={article.articleNumber || ""}
                          onChange={(e) => {
                            const selectedTemplate = articleTemplateListProduct.find(t => t.articleNumber === e.target.value);
                            if (selectedTemplate) {
                              articleTemplateHandler(article.rowNumber, selectedTemplate.id, ArticleType.Product);
                            }
                          }}
                        >
                          <option value="">- Välj mall -</option>
                          {articleTemplateListProduct && articleTemplateListProduct.map((template) => (
                            <option key={template.id} value={template.articleNumber}>
                              {template.articleNumber} - {template.description}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    )}
                    <td>
                      <Form.Control
                        type="text"
                        value={article.articleNumber}
                        size="sm"
                        onChange={(e) =>
                          articleTableHandlers.onArticleNumberChange(
                            article.rowNumber,
                            e.target.value
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={article.description}
                        size="sm"
                        onChange={(e) =>
                          articleTableHandlers.onDescriptionChange(
                            article.rowNumber,
                            e.target.value
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={article.quantity}
                        size="sm"
                        onChange={(e) =>
                          articleTableHandlers.onQuantityChange(
                            article.rowNumber,
                            parseInt(e.target.value)
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        defaultValue={article.salesPrice ?? 0}
                        value={article.salesPrice}
                        size="sm"
                        onChange={(e) =>
                          articleTableHandlers.onSalesPriceChange(
                            article.rowNumber,
                            e.target.value
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    {(clientType === ClientType.Standard || clientType === ClientType.Fleet) && (
                      <td>
                        <Form.Control
                          type="text"
                          defaultValue={article.discount ?? 0}
                          value={article.discount ?? ""}
                          size="sm"
                          onChange={(e) =>
                            articleTableHandlers.onDiscountChange(
                              article.rowNumber,
                              e.target.value
                            )
                          }
                          readOnly={article.verified}
                          disabled={article.verified}
                        ></Form.Control>
                      </td>
                    )}
                    {clientType === ClientType.Fleet && (
                      <td align="center">
                        <Form.Check
                          type="switch"
                          checked={article.excludeFromWarehousePicking || false}
                          onChange={(e) => articleTableHandlers.onExcludeChange(article.rowNumber, e.target.checked)}
                        />
                      </td>
                    )}
                    <td
                      onClick={() => articleModalHandlers.onOpen(article)}
                      role="button"
                    >
                      <Pen />
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Col>

        <EditableArticleModal
          show={showArticleModal}
          article={modalArticle}
          clientType={clientType}
          suppliers={suppliers}
          isExported={orderData.exported}
          handlers={articleModalHandlers}
          onHide={articleModalHandlers.onClose}
        />
        {!orderData.exported && (
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={addNewArticleHandler}
          >
            Lägg till artikel <Plus />
          </Button>
        )}

        {clientType === ClientType.Fleet && (
          <Col xs={12} md={12} className="mb-3">
            <>
              <h3>Reparationstider</h3>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>Art.nr</th>
                    <th>Beskrivning</th>
                    <th>Antal</th>
                    <th>Försäljningspris</th>
                    <th>Tid</th>
                  </tr>
                </thead>
                <tbody>
                  {orderData.articles &&
                    orderData.articles
                      .filter((article) => article.articleType === ArticleType.Time)
                      .map((article) => (
                        <tr key={article.rowNumber}>
                          <td>
                            <Form.Select
                              size="sm"
                              className="mt-1"
                              value={article.articleNumber || ""}
                              onChange={(e) => {
                                const selectedTemplate = articleTemplateListTime.find(t => t.articleNumber === e.target.value);
                                if (selectedTemplate) {
                                  articleTemplateHandler(article.rowNumber, selectedTemplate.id, ArticleType.Time);
                                }
                              }}
                            >
                              <option value="">- Välj mall -</option>
                              {articleTemplateListTime && articleTemplateListTime.map((template) => (
                                <option key={template.id} value={template.articleNumber}>
                                  {template.articleNumber} - {template.description}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={article.description}
                              size="sm"
                              onChange={(e) =>
                                articleTableHandlers.onDescriptionChange(
                                  article.rowNumber,
                                  e.target.value
                                )
                              }
                              readOnly={orderData.exported || article.verified}
                              disabled={orderData.exported || article.verified}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.quantity}
                              size="sm"
                              onChange={(e) =>
                                articleTableHandlers.onQuantityChange(
                                  article.rowNumber,
                                  parseInt(e.target.value)
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.salesPrice}
                              size="sm"
                              readOnly
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.time}
                              size="sm"
                              onChange={(e) =>
                                articleTableHandlers.onTimeChange(
                                  article.rowNumber,
                                  parseInt(e.target.value)
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                </tbody>
              </Table>
            </>
            <Button
              variant="outline-primary"
              className="mb-3"
              onClick={addNewTimeArticleHandler}
            >
              Lägg till tid <Plus />
            </Button>
          </Col>

        )}

        <hr />

        <div className="text-end">
          <Button
            variant="primary"
            className="mb-3"
            onClick={handleAddOrder}
            disabled={isWaitingOrderPostResponse}
          >
            Lägg till order
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>
            {orderData.customerId === ""
              ? "Du måste välja en kund innan du kan spara ordern."
              : "Något gick fel, kontrollera uppgifterna och försök igen."}
          </p>
        </Alert>
      </Form>
    </div>
  );
};

export default OrderAdd;
